import { IShopItem, IShopItemByBenefit } from 'types/income';
import i18n from 'i18n';
import { isDefined } from 'utils/isDefined';
import { converters as tableConverters } from 'store/table';
import { converters as filesConverters } from 'store/files';
import { loaders } from './shop.loaders';
import {
  BaseShopItem,
  ShopItem,
  ShopItemByBenefit,
  shopItemTypes,
} from './shop.types';

export const convertBaseShopItem = (
  item: IShopItem,
  isSelected?: boolean,
): BaseShopItem => {
  return {
    id: item.id,
    name: item.name,
    type:
      item.type && shopItemTypes.includes(item.type) ? item.type : 'GIFTCARD',
    image: item.image ? filesConverters.getUploadedFile(item.image) : null,
    sku: item.sku,
    vatRate: item.vatRate ?? 0,
    currency: item.currency,
    countries: item.countries,
    cities: item.cities ?? [],
    countriesAlpha2: item.countriesAlpha2,
    benefitPlanTypes: item.benefitPlanTypes ?? [],
    categories: item.categories,
    status: item.status,
    translations: item.translations.map((translation) => ({
      language: translation.language,
      id: translation.id,
      title: translation.title,
      description: translation.description,
    })),
    supplier: {
      id: item.supplier.id,
      name: [
        item.supplier.name,
        item.supplier.isParentCompany ? `(${i18n.t('common.private')})` : null,
      ]
        .filter(Boolean)
        .join(' '),
      logo: item.supplier.logo
        ? filesConverters.getUploadedFile(item.supplier.logo)
        : null,
      termsAndConditionsLink: item.supplier.termsAndConditionsLink ?? '',
      additionalInfo: item.supplier.additionalInfo ?? '',
      isParentCompany: item.supplier.isParentCompany,
    },
    priority: item.priorityIndex ?? 0,
    searchTags: item.searchTags ?? '',
    resolutionDuration: item.resolutionDuration ?? NaN,
    resolutionDurationUnit: item.resolutionDurationUnit ?? 'hours',
    inSelection: (isSelected || item.inSelection) ?? false,
    createdAt: item.creationDate,
    taxExemptions: item.taxExemptions,
  };
};

export const convertShopItem = (
  item: IShopItem,
  isSelected?: boolean,
): ShopItem => {
  return {
    ...convertBaseShopItem(item, isSelected),
    prices:
      item.prices
        ?.toSorted((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((price) => ({
          id: price.id,
          price: price.price,
          priceRangeFrom: price.priceRangeFrom,
          priceRangeTo: price.priceRangeTo,
          priceType: price.priceType,
          discountPercentage: price.discountPercentage,
          translations: item.translations.map((trans) => {
            const priceTranslation = price.translations?.find(
              (priceTrans) => priceTrans.language === trans.language,
            );
            return {
              language: trans.language,
              description: priceTranslation?.description ?? '',
            };
          }),
          inventory: price.inventory ?? 0,
          index: price.index ?? 0,
        })) ?? [],
  };
};

export const convertShopItemByBenefit = (
  item: IShopItemByBenefit,
): ShopItemByBenefit => {
  return {
    ...convertShopItem(item),
    assignment: item.assignment
      ? item.assignment
      : isDefined(item.autoAssigned)
      ? { autoAssigned: item.autoAssigned }
      : null,
  };
};

const convertShopItemsByBenefit = (
  items: IShopItemByBenefit[],
): ShopItemByBenefit[] => {
  return items.map(convertShopItemByBenefit);
};

const convertShopItems = (
  items: IShopItem[],
  isSelected?: boolean,
): ShopItem[] => {
  return items.map((item) => convertShopItem(item, isSelected));
};

export const converters = {
  getShopItems: (
    { data }: Awaited<ReturnType<typeof loaders.getShopItems>>,
    isSelected?: boolean,
  ) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertShopItems(data.data, isSelected),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getShopItemsByBenefit: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getShopItemsByBenefit>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertShopItemsByBenefit(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
};
